import { useCallback, useEffect, useState } from 'react';
import { visualPromptRepository } from '@/handlers/visualPrompts';
import type { VisualPromptListFilters } from '@/handlers/visualPrompts';
import type { VisualPromptDefinition } from '@/types/visualPrompts/visualPrompt.types';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { setFeaturedOverride } from '@/utils/visualPrompts/featuredVisualPromptStorage';
import { normalizeVisualPromptError } from './utils';

export const useVisualPrompts = (workspaceId?: string) => {
  const { handleError } = useWorkspaceErrorHandler(workspaceId);
  const [prompts, setPrompts] = useState<VisualPromptDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<VisualPromptListFilters>({
    category: 'all',
    search: '',
  });

  const fetchPrompts = useCallback(
    async (overrideFilters?: VisualPromptListFilters) => {
      const activeFilters = overrideFilters ?? filters;
      setIsLoading(true);
      try {
        const data = await visualPromptRepository.getAll(activeFilters);
        setPrompts(data);
        setError(null);
      } catch (err) {
        const normalizedError = normalizeVisualPromptError(err);
        console.error('[useVisualPrompts] Load Failed:', normalizedError);
        setError(normalizedError);
        handleError(normalizedError, VISUAL_PROMPT_MESSAGES.loadError);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, handleError],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await visualPromptRepository.getAll(filters);
        if (!cancelled) {
          setPrompts(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const normalizedError = normalizeVisualPromptError(err);
          console.error('[useVisualPrompts] Load Failed:', normalizedError);
          setError(normalizedError);
          handleError(normalizedError, VISUAL_PROMPT_MESSAGES.loadError);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [filters.category, filters.search, handleError]);

  const updateFilters = useCallback((updates: Partial<VisualPromptListFilters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const toggleFeatured = useCallback(
    (definition: VisualPromptDefinition) => {
      const next = !definition.isFeatured;
      setFeaturedOverride(definition.id, next);
      setPrompts((prev) =>
        prev.map((p) =>
          p.id === definition.id ? { ...p, isFeatured: next } : p,
        ),
      );
    },
    [],
  );

  return {
    prompts,
    isLoading,
    error,
    filters,
    fetchPrompts,
    updateFilters,
    toggleFeatured,
  };
};
